import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ProductOrderListSortCol, TProductOrderList, TProductOrderListResponse, TAccountProductOrderListQuery, TProductOrderListSortCol, adaptProductOrderFromListResponse, adaptProductOrderListQueryToRequest } from '../../types';
import { BACKEND_ACCOUNT_PRODUCT_ORDER } from '../../backend-paths';


export const accountProductOrderListGetApi = createAsyncThunk(
	BACKEND_ACCOUNT_PRODUCT_ORDER,
	(payload: TAccountProductOrderListQuery | undefined, thunkApi) => {
		const params = adaptProductOrderListQueryToRequest(payload);

		return HttpService.get<TProductOrderListResponse>(BACKEND_ACCOUNT_PRODUCT_ORDER, { params }).then(result => {
			return adaptProductOrderFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductOrderList;
	query: TAccountProductOrderListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ProductOrderListSortCol.ID,
	},
	error: undefined,
};

export const accountProductOrderListSlice = createSlice({
	name: 'accountProductOrderListSlice',
	initialState,
	reducers: {
		setAccountProductOrderListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountProductOrderListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountProductOrderListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountProductOrderListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountProductOrderListQuerySortCol(state, action: PayloadAction<TProductOrderListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearAccountProductOrderList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},

		setAccountProductOrderList(state, action: PayloadAction<TProductOrderList>) {
			return { ...initialState, list: action.payload, query: state.query };
		},
	},

	extraReducers: {
		[accountProductOrderListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountProductOrderListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductOrderList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountProductOrderListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountProductOrderListSelector = () => useAppSelector(state => state[accountProductOrderListSlice.name]);

export const { setAccountProductOrderList, clearAccountProductOrderList, setAccountProductOrderListQueryLimit, setAccountProductOrderListQueryOffset, setAccountProductOrderListQuerySort, setAccountProductOrderListQuerySortCol, setAccountProductOrderListQuerySearch } = accountProductOrderListSlice.actions;
